// @ts-nocheck
import React, { useState, useMemo, useRef } from 'react';
import { useLocalization, type Language } from '../../config/localization';
import Icon, { ICONS } from './icons';

interface WidgetType {
  id: number;
  name: string;
  description: string;
  icon?: string;
}

interface WorkflowWidget {
  id: number;
  workflow_id: number;
  widget_type_id: number;
  is_enabled: boolean;
  display_order: number;
  config_data?: string;
  widget_type: WidgetType;
}

// Embedded dynamic widget configuration
interface DynamicWidgetConfig {
  name: string;
  description?: string | null;
  display_order: number;
  fields: Array<{
    field_id: string;
    field_type: string;
    label: string;
  }>;
}

interface WorkflowWidgetEditorProps {
  widgetTypes: WidgetType[];
  selectedLanguage: Language;

  // For Create mode (simple selection with order)
  selectedWidgetIds?: string[];
  onWidgetToggle?: (widgetId: string) => void;
  selectedWidgetOrders?: Record<string, number>;
  onCreateDisplayOrderChange?: (widgetTypeId: string, newOrder: number) => void;

  // For Edit mode (advanced with display order)
  workflowWidgets?: WorkflowWidget[];
  onAdvancedWidgetToggle?: (widgetTypeId: string) => void;
  onDisplayOrderChange?: (widgetTypeId: string, newOrder: number) => void;

  // Embedded dynamic widget (only for edit mode)
  dynamicWidget?: DynamicWidgetConfig | null;
  onDynamicWidgetClick?: () => void;
  onDynamicWidgetOrderChange?: (newOrder: number) => void;

  // Mode
  mode: 'create' | 'edit';
}

type DragSource = 'available' | 'enabled';

// Special ID for the embedded dynamic widget
const DYNAMIC_WIDGET_ID = -999;

const WorkflowWidgetEditor: React.FC<WorkflowWidgetEditorProps> = ({
  widgetTypes,
  selectedLanguage,
  selectedWidgetIds = [],
  onWidgetToggle,
  selectedWidgetOrders = {},
  onCreateDisplayOrderChange,
  workflowWidgets = [],
  onAdvancedWidgetToggle,
  onDisplayOrderChange,
  dynamicWidget,
  onDynamicWidgetClick,
  onDynamicWidgetOrderChange,
  mode
}) => {
  const t = useLocalization(selectedLanguage);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedWidget, setDraggedWidget] = useState<{ id: number; source: DragSource } | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<{ zone: DragSource; index?: number } | null>(null);
  const enabledListRef = useRef<HTMLDivElement>(null);

  // Get enabled widgets sorted by display order (including embedded dynamic widget)
  const enabledWidgets = useMemo(() => {
    if (mode === 'create') {
      return selectedWidgetIds
        .map((widgetId) => {
          const widgetType = widgetTypes.find(wt => wt.id === Number(widgetId));
          const displayOrder = selectedWidgetOrders[widgetId] || 0;
          return widgetType ? { widgetType, displayOrder, isDynamic: false } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    } else {
      // Get regular widgets
      const regularWidgets = workflowWidgets
        .filter(w => w.is_enabled)
        .map(widget => ({
          widgetType: widget.widget_type,
          displayOrder: widget.display_order,
          isDynamic: false
        }));

      // Add embedded dynamic widget if it exists and has a name
      if (dynamicWidget?.name && dynamicWidget.fields && dynamicWidget.fields.length > 0) {
        regularWidgets.push({
          widgetType: {
            id: DYNAMIC_WIDGET_ID,
            name: dynamicWidget.name,
            description: dynamicWidget.description || `${dynamicWidget.fields.length} fields`,
            icon: '⚙️'
          },
          displayOrder: dynamicWidget.display_order,
          isDynamic: true
        });
      }

      return regularWidgets.sort((a, b) => a.displayOrder - b.displayOrder);
    }
  }, [mode, selectedWidgetIds, selectedWidgetOrders, workflowWidgets, widgetTypes, dynamicWidget]);

  // Get available (not enabled) widgets
  const availableWidgets = useMemo(() => {
    const enabledIds = new Set(enabledWidgets.map(w => w.widgetType.id));
    let available = widgetTypes.filter(wt => !enabledIds.has(wt.id));

    if (searchTerm.trim()) {
      available = available.filter(widget =>
        widget.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        widget.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return available;
  }, [widgetTypes, enabledWidgets, searchTerm]);

  // Enable a widget
  const enableWidget = (widgetId: number, insertAtIndex?: number) => {
    if (mode === 'create' && onWidgetToggle) {
      onWidgetToggle(String(widgetId));
      // Set display order based on insert position
      if (onCreateDisplayOrderChange && insertAtIndex !== undefined) {
        // Shift other widgets' orders and insert at position
        const newOrder = insertAtIndex + 1;
        onCreateDisplayOrderChange(String(widgetId), newOrder);
      }
    } else if (mode === 'edit' && onAdvancedWidgetToggle) {
      onAdvancedWidgetToggle(String(widgetId));
      // Set display order based on insert position
      if (onDisplayOrderChange && insertAtIndex !== undefined) {
        const newOrder = insertAtIndex + 1;
        onDisplayOrderChange(String(widgetId), newOrder);
      }
    }
  };

  // Disable a widget
  const disableWidget = (widgetId: number) => {
    if (mode === 'create' && onWidgetToggle) {
      onWidgetToggle(String(widgetId));
    } else if (mode === 'edit' && onAdvancedWidgetToggle) {
      onAdvancedWidgetToggle(String(widgetId));
    }
  };

  // Reorder widgets in enabled list
  const reorderWidgets = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const reordered = [...enabledWidgets];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // Update all display orders
    reordered.forEach((widget, index) => {
      const newOrder = index + 1;

      // Handle embedded dynamic widget separately
      if (widget.isDynamic) {
        if (onDynamicWidgetOrderChange) {
          onDynamicWidgetOrderChange(newOrder);
        }
        return;
      }

      if (mode === 'create' && onCreateDisplayOrderChange) {
        onCreateDisplayOrderChange(String(widget.widgetType.id), newOrder);
      } else if (mode === 'edit' && onDisplayOrderChange) {
        onDisplayOrderChange(String(widget.widgetType.id), newOrder);
      }
    });
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, widgetId: number, source: DragSource) => {
    setDraggedWidget({ id: widgetId, source });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(widgetId));

    // Add drag image styling
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedWidget(null);
    setDragOverTarget(null);
  };

  const handleDragOver = (e: React.DragEvent, zone: DragSource, index?: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTarget({ zone, index });
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverTarget(null);
    }
  };

  const handleDropOnEnabled = (e: React.DragEvent, dropIndex?: number) => {
    e.preventDefault();

    if (!draggedWidget) return;

    if (draggedWidget.source === 'available') {
      // Moving from available to enabled
      enableWidget(draggedWidget.id, dropIndex ?? enabledWidgets.length);
    } else if (draggedWidget.source === 'enabled' && dropIndex !== undefined) {
      // Reordering within enabled
      const fromIndex = enabledWidgets.findIndex(w => w.widgetType.id === draggedWidget.id);
      if (fromIndex !== -1 && fromIndex !== dropIndex) {
        reorderWidgets(fromIndex, dropIndex);
      }
    }

    setDraggedWidget(null);
    setDragOverTarget(null);
  };

  const handleDropOnAvailable = (e: React.DragEvent) => {
    e.preventDefault();

    if (!draggedWidget) return;

    if (draggedWidget.source === 'enabled') {
      // Moving from enabled to available (disable)
      disableWidget(draggedWidget.id);
    }

    setDraggedWidget(null);
    setDragOverTarget(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[calc(100vh-400px)] min-h-[400px]">
      {/* Available Widgets (Left Column) */}
      <div
        className={`flex flex-col h-full rounded-xl border-2 transition-colors ${
          dragOverTarget?.zone === 'available' && draggedWidget?.source === 'enabled'
            ? 'border-orange-500 bg-orange-900/10'
            : 'border-gray-700 bg-gray-800/30'
        }`}
        onDragOver={(e) => handleDragOver(e, 'available')}
        onDragLeave={handleDragLeave}
        onDrop={handleDropOnAvailable}
      >
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.GRID} size={18} className="text-gray-400" />
            <span className="text-sm font-medium text-gray-300">
              {t.admin?.availableWidgets || 'Available Widgets'}
            </span>
            <span className="px-2 py-0.5 bg-gray-700 rounded-full text-xs text-gray-400">
              {availableWidgets.length}
            </span>
          </div>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-gray-700/50">
          <div className="relative">
            <Icon name={ICONS.SEARCH} size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder={t.admin?.searchWidgets || 'Search...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
              >
                <Icon name={ICONS.X} size={12} />
              </button>
            )}
          </div>
        </div>

        {/* Widget list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {availableWidgets.length > 0 ? (
            availableWidgets.map((widget) => (
              <div
                key={widget.id}
                draggable
                onDragStart={(e) => handleDragStart(e, widget.id, 'available')}
                onDragEnd={handleDragEnd}
                className={`group flex items-center gap-2 p-2.5 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                  draggedWidget?.id === widget.id
                    ? 'bg-purple-900/30 border-purple-500 opacity-50'
                    : 'bg-gray-800/50 border-gray-600/50 hover:border-gray-500 hover:bg-gray-800'
                }`}
              >
                <Icon name={ICONS.GRIP} size={14} className="text-gray-600 flex-shrink-0" />
                <span className="text-lg flex-shrink-0">{widget.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{widget.name}</div>
                  {widget.description && (
                    <div className="text-xs text-gray-500 truncate">{widget.description}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    enableWidget(widget.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-green-600/20 text-green-400 hover:bg-green-600/40 transition-all"
                  title={t.admin?.enableWidget || 'Enable widget'}
                >
                  <Icon name={ICONS.PLUS} size={14} />
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Icon name={ICONS.CHECK} size={32} className="mx-auto mb-2 text-green-600/50" />
              <p className="text-sm">
                {searchTerm
                  ? (t.admin?.noWidgetsFound || 'No widgets found')
                  : (t.admin?.allWidgetsEnabled || 'All widgets enabled')
                }
              </p>
            </div>
          )}
        </div>

        {/* Hint */}
        <div className="px-3 py-2 border-t border-gray-700/50 bg-gray-800/30">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Icon name={ICONS.INFO} size={12} />
            {t.admin?.dragToEnableHint || 'Drag to right to enable'}
          </p>
        </div>
      </div>

      {/* Enabled Widgets (Right Column) */}
      <div
        ref={enabledListRef}
        className={`flex flex-col h-full rounded-xl border-2 transition-colors ${
          dragOverTarget?.zone === 'enabled'
            ? 'border-green-500 bg-green-900/10'
            : 'border-gray-700 bg-gray-800/30'
        }`}
        onDragOver={(e) => handleDragOver(e, 'enabled')}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDropOnEnabled(e)}
      >
        <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name={ICONS.CHECK} size={18} className="text-green-400" />
            <span className="text-sm font-medium text-gray-300">
              {t.admin?.enabledWidgets || 'Enabled Widgets'}
            </span>
            <span className="px-2 py-0.5 bg-green-900/50 border border-green-500/30 rounded-full text-xs text-green-400">
              {enabledWidgets.length}
            </span>
          </div>
        </div>

        {/* Enabled widget list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {enabledWidgets.length > 0 ? (
            enabledWidgets.map((item, index) => {
              const isDropTarget = dragOverTarget?.zone === 'enabled' && dragOverTarget?.index === index;
              const isDragging = draggedWidget?.id === item.widgetType.id && draggedWidget?.source === 'enabled';
              const isDynamicWidget = item.isDynamic;

              return (
                <div
                  key={item.widgetType.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.widgetType.id, 'enabled')}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOverTarget({ zone: 'enabled', index });
                  }}
                  onDrop={(e) => {
                    e.stopPropagation();
                    handleDropOnEnabled(e, index);
                  }}
                  onClick={isDynamicWidget && onDynamicWidgetClick ? onDynamicWidgetClick : undefined}
                  className={`group flex items-center gap-2 p-2.5 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                    isDragging
                      ? 'bg-purple-900/30 border-purple-500 opacity-50 scale-95'
                      : isDropTarget
                      ? 'bg-blue-900/30 border-blue-500 border-2'
                      : isDynamicWidget
                      ? 'bg-blue-900/30 border-blue-500/50 hover:border-blue-400 hover:bg-blue-900/40'
                      : 'bg-gray-800/80 border-green-500/30 hover:border-green-500/50'
                  }`}
                >
                  <span className="text-xs text-gray-500 font-mono w-5 flex-shrink-0">#{index + 1}</span>
                  <Icon name={ICONS.GRIP} size={14} className="text-gray-500 flex-shrink-0" />
                  {isDynamicWidget ? (
                    <Icon name={ICONS.SLIDERS} size={18} className="text-blue-400 flex-shrink-0" />
                  ) : (
                    <span className="text-lg flex-shrink-0">{item.widgetType.icon}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${isDynamicWidget ? 'text-blue-300' : 'text-white'}`}>
                        {item.widgetType.name}
                      </span>
                      {isDynamicWidget && (
                        <span className="px-1.5 py-0.5 bg-blue-600/30 border border-blue-500/40 rounded text-[10px] text-blue-300 flex-shrink-0">
                          {t.admin?.embedded || 'Embedded'}
                        </span>
                      )}
                    </div>
                    {item.widgetType.description && (
                      <div className="text-xs text-gray-500 truncate">{item.widgetType.description}</div>
                    )}
                  </div>
                  {isDynamicWidget ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onDynamicWidgetClick) onDynamicWidgetClick();
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-blue-600/20 text-blue-400 hover:bg-blue-600/40 transition-all"
                      title={t.admin?.editWidget || 'Edit widget'}
                    >
                      <Icon name={ICONS.EDIT} size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        disableWidget(item.widgetType.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-red-600/20 text-red-400 hover:bg-red-600/40 transition-all"
                      title={t.admin?.disableWidget || 'Disable widget'}
                    >
                      <Icon name={ICONS.X} size={14} />
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div
              className={`text-center py-12 rounded-lg border-2 border-dashed transition-colors ${
                dragOverTarget?.zone === 'enabled'
                  ? 'border-green-500 bg-green-900/20'
                  : 'border-gray-600'
              }`}
            >
              <Icon name={ICONS.PLUS} size={32} className="mx-auto mb-2 text-gray-600" />
              <p className="text-sm text-gray-500">
                {t.admin?.dropWidgetsHere || 'Drag widgets here to enable'}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {t.admin?.orClickPlus || 'or click + button'}
              </p>
            </div>
          )}
        </div>

        {/* Hint */}
        <div className="px-3 py-2 border-t border-gray-700/50 bg-gray-800/30">
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <Icon name={ICONS.INFO} size={12} />
            {t.admin?.dragToReorderHint || 'Drag to reorder, drag left to disable'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkflowWidgetEditor;
